import type { jsPDF } from "jspdf";
import { C1_DOCUMENT_LOGO } from "../../ConceptOne/src/C1_Const";
import { loadPdfDocumentLogo } from "./pdfDocumentLogo";

export type PdfHeaderSource = {
  projectLicense?: { checkIfProfessionalAbo(): boolean };
  settings?: {
    applicationMode?: string;
    companyName?: string;
    companyUrl?: string;
    companyLogo?: string;
    companyLogoOrDefault?: string;
  };
};
export type PdfDocumentBrand = { name: string; logoSource: string; url?: string };
export type PdfHeaderContent = {
  planType: string | readonly [string, string];
  hierarchy?: readonly [string, string];
};
export const PDF_HEADER_BOTTOM = 70;
export const PDF_HEADER_CONTENT_TOP = 90;

/** Accepts website addresses with or without a scheme, excluding non-web PDF actions. */
export function companyWebsiteUrl(value: string): string | undefined {
  const text = value.trim() || "estada.de";
  try {
    const url = new URL(/^[a-z][a-z\d+.-]*:/i.test(text) ? text : `https://${text}`);
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : undefined;
  } catch {
    return undefined;
  }
}

/** Resolves branding from the active application, retaining the legacy custom-logo entitlement. */
export function resolvePdfDocumentBrand(source?: PdfHeaderSource): PdfDocumentBrand {
  const settings = source?.settings;
  const licensed = source?.projectLicense?.checkIfProfessionalAbo() ?? false;
  const defaultLogo = settings?.applicationMode === "c1" ? C1_DOCUMENT_LOGO : "/images/logo-detail-small-2.png";
  const customLogo = licensed ? settings?.companyLogo : undefined;
  const permittedLogo = customLogo && settings?.companyLogoOrDefault === customLogo ? customLogo : undefined;
  const companyName = licensed ? settings?.companyName?.trim() || "ESTADA" : "ESTADA";
  const name = companyName.toLowerCase() === "estada" ? "ESTADA" : companyName;
  const url = companyWebsiteUrl(licensed ? settings?.companyUrl || "estada.de" : "estada.de");
  return { name, logoSource: permittedLogo || defaultLogo, url };
}

/** Shrinks a single header line before abbreviating it, keeping all three columns separate. */
function fittedHeaderText(doc: jsPDF, text: string, width: number, preferredSize: number): string {
  doc.setFontSize(preferredSize);
  const measured = doc.getTextWidth(text);
  if (measured > width) doc.setFontSize(Math.max(6, preferredSize * width / measured));
  if (doc.getTextWidth(text) <= width) return text;
  let fitted = text;
  while (fitted && doc.getTextWidth(`${fitted}…`) > width) fitted = fitted.slice(0, -1);
  return fitted ? `${fitted}…` : "";
}

/** Owns the complete document header geometry; exporters supply only content and branding. */
export class PdfDocumentHeader {
  constructor(readonly brand: PdfDocumentBrand, private readonly logo?: HTMLImageElement) {}

  draw(doc: jsPDF, content: PdfHeaderContent): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 45;
    const columnWidth = (pageWidth - margin * 2) / 3;
    const textWidth = columnWidth - 16;
    const centerY = 45;
    doc.saveGraphicsState();
    doc.setTextColor("#172b3a");
    if (this.logo) {
      const naturalWidth = this.logo.naturalWidth || this.logo.width || 1;
      const naturalHeight = this.logo.naturalHeight || this.logo.height || 1;
      const scale = 34 / Math.max(naturalWidth, naturalHeight);
      const width = naturalWidth * scale, height = naturalHeight * scale;
      doc.addImage(this.logo, margin + (34 - width) / 2, centerY - height / 2, width, height);
    }
    doc.setFont("helvetica", "bold");
    const name = fittedHeaderText(doc, this.brand.name, columnWidth - 51, 15.625);
    doc.text(name, margin + 43, centerY, { baseline: "middle" });
    if (this.brand.url) {
      doc.link(margin, centerY - 19, 43 + doc.getTextWidth(name) + 2, 38, { url: this.brand.url });
    }

    // Title wrapping uses a stable size, independently of the company-name length.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    const titleLines = typeof content.planType === "string"
      ? doc.splitTextToSize(content.planType, textWidth) as string[] : [...content.planType];
    const title = titleLines.length > 2 ? [titleLines[0], titleLines.slice(1).join(" ")] : titleLines;
    for (const [index, line] of title.entries()) {
      doc.setFont("helvetica", "bold");
      const fitted = fittedHeaderText(doc, line, textWidth, 11);
      doc.text(fitted, pageWidth / 2, title.length > 1 ? centerY - 7 + index * 14 : centerY,
        { align: "center", baseline: "middle" });
    }
    const hierarchy = content.hierarchy ?? ["", ""];
    for (const [index, line] of hierarchy.entries()) {
      if (!line.trim()) continue;
      doc.setFont("helvetica", "bold");
      const fitted = fittedHeaderText(doc, line, textWidth, index === 0 ? 11 : 10);
      const y = index === 0 && !hierarchy[1].trim() ? centerY : centerY - 7 + index * 14;
      doc.text(fitted, pageWidth - margin, y, { align: "right", baseline: "middle" });
    }
    doc.setDrawColor("#aeb9bf");
    doc.setLineWidth(0.6);
    doc.setLineDashPattern([], 0);
    doc.line(margin, PDF_HEADER_BOTTOM, pageWidth - margin, PDF_HEADER_BOTTOM);
    doc.restoreGraphicsState();
    return PDF_HEADER_CONTENT_TOP;
  }
}

/** Snapshots the active app's branding and loads its image once for all pages in an export. */
export async function loadPdfDocumentHeader(source: PdfHeaderSource | undefined = globalThis.state): Promise<PdfDocumentHeader> {
  const brand = resolvePdfDocumentBrand(source);
  const logo = await loadPdfDocumentLogo(brand.logoSource);
  return new PdfDocumentHeader(brand, logo);
}
